// Formatters for the V2 Trades grid columns sourced from
// ClosedTradeProjection. Decimal values cross the wire as strings
// (Python Decimal -> JSON string); these helpers parse + format them
// for the TradesPage cells. Each returns "—" for null / undefined so
// the grid renders OPEN rows (where projection fields are null) and
// CLOSED rows missing optional pieces (no planned_risk -> r_multiple
// is null) uniformly.

import type { components } from "../api/schema";

type FillAction = components["schemas"]["FillAction"];

const EM_DASH = "—";
const DECIMAL_STRING = /^-?\d+(\.\d+)?$/;

// Compact action labels (operator wording) shared by the trade-detail
// Executions table and the What-If page: an opening buy is "Buy", a closing
// sell is "Sell", an opening sell (short entry) is "Short", and a closing buy
// (short cover) is "Cover". The verbose "Buy to Open" form is reserved for the
// Record-fill authoring form on the trade-detail page.
export const FILL_ACTION_EXECUTION_LABELS: Record<FillAction, string> = {
  bto: "Buy",
  stc: "Sell",
  sto: "Short",
  btc: "Cover",
};

// Map a fill action code to its compact label, tolerating either the typed
// FillAction or a raw lower/upper-case wire string. Unknown codes fall back to
// the input verbatim.
export function formatFillAction(action: string): string {
  return FILL_ACTION_EXECUTION_LABELS[action.toLowerCase() as FillAction] ?? action;
}

function asNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!DECIMAL_STRING.test(value)) {
    return null;
  }
  return Number(value);
}

// Format a fractional decimal as a signed percentage with 2 decimal
// places, e.g. "0.15" -> "+15.00%", "-0.0825" -> "-8.25%". `null`
// (or non-numeric input) renders the em-dash placeholder.
export function formatPercent(value: string | null | undefined): string {
  const numeric = asNumber(value);
  if (numeric === null) {
    return EM_DASH;
  }
  const pct = numeric * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

// Format an R-multiple as a signed decimal with one decimal place and
// the literal "R" suffix, e.g. "3" -> "+3.0R", "-0.5" -> "-0.5R".
// `null` (no planned_risk or projection absent) renders the em-dash.
export function formatRMultiple(value: string | null | undefined): string {
  const numeric = asNumber(value);
  if (numeric === null) {
    return EM_DASH;
  }
  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(1)}R`;
}

// Format an integer seconds count as a human-readable hold time.
// < 60s -> "Ns"; < 1h -> "Nm"; < 1d -> "Nh Mm"; >= 1d -> "Nd Mh".
// 0 -> "0s" (intra-second close). `null` renders the em-dash.
export function formatHoldDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) {
    return EM_DASH;
  }
  const totalSeconds = Math.max(0, Math.floor(seconds));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    return `${minutes}m`;
  }
  if (totalSeconds < 86_400) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
  }
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  return hours === 0 ? `${days}d` : `${days}d ${hours}h`;
}

// Render a share / contract volume for the Trades-grid Volume column.
// Decimal strings cross the wire (Python Decimal); render with no
// grouping separators and trailing-zero-trimmed fractional precision so
// whole-share volumes show as plain integers ("12000") and fractional
// (forex) volumes keep up to 4 places. `null` renders the em-dash.
export function formatQuantity(value: string | null | undefined): string {
  const numeric = asNumber(value);
  if (numeric === null) {
    return EM_DASH;
  }
  return new Intl.NumberFormat("en-US", {
    useGrouping: false,
    maximumFractionDigits: 4,
  }).format(numeric);
}

// Render the tag-labels CSV cell. Empty string ("" — no live tags) and
// null both collapse to the em-dash; otherwise the comma-joined string
// is returned verbatim for the grid cell.
export function formatTagLabels(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return EM_DASH;
  }
  return value;
}
